/**
 * HTTP Engine Client — connects to an Engine Gateway via HTTP/WebSocket.
 *
 * Provides an EngineService backed by:
 *   - HTTP POST /api/engine/rpc for JSON-RPC method calls
 *   - WebSocket /api/engine/ws for real-time event subscriptions
 *   - Fetch-streaming SSE /api/engine/events as fallback when WS is blocked
 *     or when a Tauri production webview cannot use browser-native transport
 *
 * Used by Editor (Tauri), Web (browser), and Play (Tauri) apps.
 * Pass a custom `fetchFn` for Tauri environments or other non-standard fetch.
 */

import type {
    GatewayConnectedEvent,
    GatewayNotificationEnvelope,
} from "@magic-md/engine-client/protocol";
import type {
    EngineNotificationHandler,
    EngineService,
    EngineStatus,
} from "../platform";
import { frontendLog } from "../utils/frontend-logger";
import { createRpcEngineService } from "./engine-rpc";

export interface HttpEngineClientOptions {
    /** Engine Gateway endpoint (e.g. "http://localhost:3721") */
    endpoint: string;
    /** Optional bearer token for authentication */
    token?: string;
    /** Request timeout in milliseconds (default: 30000) */
    requestTimeout?: number;
    /**
     * Custom fetch function. Defaults to `globalThis.fetch`.
     * Pass Tauri's fetch wrapper for desktop/mobile apps.
     */
    fetchFn?: typeof globalThis.fetch;
    /** Stable logical Gateway client identity. Generated once when omitted. */
    clientId?: string;
}

function createGatewayClientId(): string {
    if (typeof globalThis.crypto?.randomUUID === "function") {
        return globalThis.crypto.randomUUID();
    }
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
        const value = Math.floor(Math.random() * 16);
        return (char === "x" ? value : (value & 0x3) | 0x8).toString(16);
    });
}

function createEngineRpcError(
    error: { code: number; message: string; data?: unknown },
    httpStatus: number,
): Error & { code: number; data?: unknown; httpStatus: number } {
    return Object.assign(new Error(error.message), {
        name: "EngineRpcError",
        code: error.code,
        data: error.data,
        httpStatus,
    });
}

/**
 * Create an EngineService backed by HTTP/WebSocket to an Engine Gateway.
 */
export function createHttpEngineClient(
    options: HttpEngineClientOptions,
): EngineService {
    const {
        endpoint,
        token,
        requestTimeout = 30_000,
        fetchFn = globalThis.fetch.bind(globalThis),
        clientId = createGatewayClientId(),
    } = options;
    const rpcUrl = `${endpoint}/api/engine/rpc`;
    const statusUrl = `${endpoint}/api/engine/status`;

    let ws: WebSocket | null = null;
    /** AbortController for fetch-streaming SSE fallback */
    let fetchSSEAbort: AbortController | null = null;
    const notificationHandlers = new Set<EngineNotificationHandler>();
    let wsReconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let wsDisconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let useSSEFallback = false;
    /** Use fetch-streaming SSE for every SSE fallback path. */
    let useFetchSSE = false;
    /** Tracks whether we've had a successful connection before (for reconnect detection) */
    let hadPriorConnection = false;
    let hadTransportDisconnect = false;
    /** Gateway event connection associated with subsequent HTTP RPC calls. */
    let gatewayClientId = clientId;
    /** Last Gateway notification sequence applied by this client. */
    let lastGatewaySequence = 0;
    /** Raw transport awaiting the authoritative Gateway handshake. */
    let pendingHandshakeTransport: string | null = null;
    let gatewayHandshakeComplete = false;
    let handshakeWaiter: {
        promise: Promise<void>;
        resolve: () => void;
        reject: (error: Error) => void;
        timer: ReturnType<typeof setTimeout>;
    } | null = null;

    function waitForGatewayHandshake(): Promise<void> {
        if (gatewayHandshakeComplete) return Promise.resolve();
        if (handshakeWaiter) return handshakeWaiter.promise;

        let resolve!: () => void;
        let reject!: (error: Error) => void;
        const promise = new Promise<void>((onResolve, onReject) => {
            resolve = onResolve;
            reject = onReject;
        });
        const timer = setTimeout(() => {
            if (handshakeWaiter?.promise !== promise) return;
            handshakeWaiter = null;
            reject(
                new Error(
                    `Engine Gateway notification handshake timed out after ${requestTimeout}ms`,
                ),
            );
        }, requestTimeout);
        handshakeWaiter = { promise, resolve, reject, timer };
        return promise;
    }

    function resolveGatewayHandshake(): void {
        const waiter = handshakeWaiter;
        if (!waiter) return;
        handshakeWaiter = null;
        clearTimeout(waiter.timer);
        waiter.resolve();
    }

    function rejectGatewayHandshake(error: Error): void {
        const waiter = handshakeWaiter;
        if (!waiter) return;
        handshakeWaiter = null;
        clearTimeout(waiter.timer);
        waiter.reject(error);
    }

    function dispatchNotification(notification: {
        method: string;
        params: unknown;
    }): void {
        let reconnectTransport: string | null = null;
        if (
            notification.method === "gateway.connected" &&
            notification.params &&
            typeof notification.params === "object" &&
            "clientId" in notification.params &&
            typeof (notification.params as { clientId?: unknown }).clientId ===
                "string"
        ) {
            gatewayClientId = (
                notification.params as { clientId: string }
            ).clientId;
            const connected = notification.params as Partial<GatewayConnectedEvent>;
            if (
                typeof connected.latestSequence === "number" &&
                Number.isSafeInteger(connected.latestSequence)
            ) {
                lastGatewaySequence = Math.max(
                    lastGatewaySequence,
                    connected.latestSequence,
                );
            }
            reconnectTransport = markGatewayConnected();
        }
        for (const handler of notificationHandlers) {
            try {
                handler(notification);
            } catch {
                // Ignore handler errors
            }
        }
        if (reconnectTransport) {
            dispatchNotification({
                method: "event/transport.reconnected",
                params: { transport: reconnectTransport },
            });
        }
    }

    function markGatewayConnected(): string | null {
        const transport = pendingHandshakeTransport;
        pendingHandshakeTransport = null;
        const shouldDispatchReconnect =
            hadPriorConnection || hadTransportDisconnect;
        hadPriorConnection = true;
        hadTransportDisconnect = false;
        gatewayHandshakeComplete = true;
        resetReconnectBackoff();
        resolveGatewayHandshake();
        return shouldDispatchReconnect ? transport : null;
    }

    function acceptGatewaySequence(sequence: number | undefined): boolean {
        if (
            sequence === undefined ||
            !Number.isSafeInteger(sequence) ||
            sequence < 0
        ) {
            return true;
        }
        if (sequence <= lastGatewaySequence) return false;
        lastGatewaySequence = sequence;
        return true;
    }

    function eventStreamUrl(path: "events" | "ws"): string {
        const base =
            path === "ws"
                ? `${endpoint.replace(/^http/, "ws")}/api/engine/ws`
                : `${endpoint}/api/engine/events`;
        const query = new URLSearchParams();
        if (token) query.set("token", token);
        query.set("clientId", gatewayClientId);
        query.set("after", String(lastGatewaySequence));
        const encoded = query.toString();
        return encoded ? `${base}?${encoded}` : base;
    }

    function dispatchTransportDisconnected(transport: string, error?: unknown): void {
        hadTransportDisconnect = true;
        gatewayHandshakeComplete = false;
        pendingHandshakeTransport = null;
        dispatchNotification({
            method: "event/transport.disconnected",
            params: {
                transport,
                ...(error
                    ? { error: error instanceof Error ? error.message : String(error) }
                    : {}),
            },
        });
    }

    // ── HTTP JSON-RPC ──

    async function rpc<T>(method: string, params?: unknown): Promise<T> {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), requestTimeout);

        try {
            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };
            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }
            headers["X-Magic-MD-Client-Id"] = gatewayClientId;

            const response = await fetchFn(rpcUrl, {
                method: "POST",
                headers,
                body: JSON.stringify({ method, params }),
                signal: controller.signal,
            });

            const text = await response.text().catch(() => "");
            let json: {
                result?: T;
                error?: { code: number; message: string; data?: unknown };
            } | undefined;
            try {
                json = text ? JSON.parse(text) : undefined;
            } catch {
                json = undefined;
            }

            if (!json) {
                if (!response.ok) {
                    throw new Error(
                        `Engine Gateway HTTP ${response.status}: ${text}`,
                    );
                }
                throw new Error("Engine Gateway returned an invalid JSON response");
            }

            if (json?.error) {
                throw createEngineRpcError(json.error, response.status);
            }

            if (!response.ok) {
                throw new Error(`Engine Gateway HTTP ${response.status}: ${text}`);
            }

            return json?.result as T;
        } finally {
            clearTimeout(timer);
        }
    }

    // ── WebSocket / SSE Event Subscription ──

    // Exponential backoff for reconnection: 500ms → 1s → 2s → 5s → 10s (max)
    const RECONNECT_DELAYS = [500, 1_000, 2_000, 5_000, 10_000];
    let reconnectAttempt = 0;
    /** Tracks consecutive WS failures without any successful message received */
    let consecutiveWsFailures = 0;
    /** Max consecutive WS failures before falling back to SSE */
    const MAX_WS_FAILURES_BEFORE_FALLBACK = 3;

    function getReconnectDelay(): number {
        const delay =
            RECONNECT_DELAYS[Math.min(reconnectAttempt, RECONNECT_DELAYS.length - 1)];
        reconnectAttempt++;
        return delay;
    }

    function resetReconnectBackoff(): void {
        reconnectAttempt = 0;
    }

    function connectStreamingSSEFallback(): void {
        // Native EventSource requires exact addEventListener(eventName) calls and
        // cannot catch dynamically named extension events such as
        // event/extension.agents.session.event. The fetch-streaming parser reads
        // the raw `event:` field, so it preserves extension runtime broadcasts.
        useFetchSSE = true;
        connectFetchSSE();
    }

    /**
     * Detect if we need SSE fallback.
     * When the page is HTTPS and the endpoint is HTTP (localhost connect),
     * browsers block ws:// as mixed content. SSE over HTTP is allowed
     * because 127.0.0.1/localhost is treated as a secure context.
     */
    function needsSSEFallback(): boolean {
        if (useSSEFallback) return true;
        if (typeof window === "undefined") return false;
        const pageIsSecure = window.location.protocol === "https:";
        const endpointIsInsecure = endpoint.startsWith("http://");
        return pageIsSecure && endpointIsInsecure;
    }

    /**
     * Detect if we're in a Tauri production webview.
     *
     * In Tauri 2 production builds on macOS, the webview uses a custom URL
     * scheme (`tauri://localhost` or `https://tauri.localhost`). Both native
     * `WebSocket` and `EventSource` are blocked by WKWebView because they
     * use the browser networking stack, which refuses connections from
     * non-standard origins. Only `fetchFn` (routed through `@tauri-apps/plugin-http`
     * → Rust `reqwest`) bypasses this restriction.
     *
     * We detect this by checking for a non-standard protocol AND a custom
     * fetchFn being provided (which signals Tauri environment).
     */
    function needsFetchSSE(): boolean {
        if (useFetchSSE) return true;
        if (typeof window === "undefined") return false;
        // If fetchFn is the same as native fetch, we're in a standard browser
        if (fetchFn === globalThis.fetch || fetchFn === globalThis.fetch?.bind(globalThis)) return false;
        const proto = window.location.protocol;
        // tauri:// or https://tauri.localhost or other non-standard origins
        return proto === "tauri:" || proto === "ipc:" ||
            (proto === "https:" && window.location.hostname === "tauri.localhost");
    }

    // ── Fetch-streaming SSE fallback ──

    /**
     * Connect to the SSE endpoint using `fetchFn` + ReadableStream.
     *
     * This handles both Tauri production builds and browser HTTPS→HTTP loopback
     * fallback. The response is consumed as a stream so dynamically named
     * extension events can be dispatched without a static EventSource listener.
     */
    async function connectFetchSSE(): Promise<void> {
        if (fetchSSEAbort) return; // already connected

        const abort = new AbortController();
        fetchSSEAbort = abort;

        const eventsUrl = eventStreamUrl("events");

        try {
            const headers: Record<string, string> = {
                Accept: "text/event-stream",
            };
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }
            const response = await fetchFn(eventsUrl, {
                headers,
                signal: abort.signal,
            });

            if (!response.ok || !response.body) {
                throw new Error(`SSE fetch failed: ${response.status}`);
            }

            frontendLog.debug("[engine-client] Fetch-SSE connected (Tauri fallback)", undefined, {
                source: "transport",
            });
            pendingHandshakeTransport = "fetch-sse";

            // Parse SSE stream
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done || abort.signal.aborted) break;

                buffer += decoder.decode(value, { stream: true });

                // SSE events are separated by double newlines
                const parts = buffer.split("\n\n");
                // Last part may be incomplete — keep it in buffer
                buffer = parts.pop() ?? "";

                for (const part of parts) {
                    if (!part.trim()) continue;
                    if (notificationHandlers.size === 0) continue;

                    let eventName = "message";
                    let dataStr = "";
                    let sequence: number | undefined;

                    for (const line of part.split("\n")) {
                        if (line.startsWith("id:")) {
                            const parsed = Number(line.slice(3).trim());
                            if (Number.isSafeInteger(parsed) && parsed >= 0) {
                                sequence = parsed;
                            }
                        } else if (line.startsWith("event: ")) {
                            eventName = line.slice(7).trim();
                        } else if (line.startsWith("data: ")) {
                            dataStr = line.slice(6);
                        } else if (line.startsWith("data:")) {
                            dataStr = line.slice(5);
                        }
                    }

                    if (dataStr) {
                        try {
                            const data = JSON.parse(dataStr);
                            if (!acceptGatewaySequence(sequence)) continue;
                            dispatchNotification({ method: eventName, params: data });
                        } catch {
                            // Ignore malformed events
                        }
                    }
                }
            }
        } catch (e) {
            if (abort.signal.aborted) return; // intentional disconnect
            dispatchTransportDisconnected("fetch-sse", e);
            frontendLog.debug(
                `[engine-client] Fetch-SSE error: ${e instanceof Error ? e.message : String(e)}`,
                undefined,
                { source: "transport" },
            );
        } finally {
            if (fetchSSEAbort === abort) {
                fetchSSEAbort = null;
            }
            if (!abort.signal.aborted) {
                gatewayHandshakeComplete = false;
                pendingHandshakeTransport = null;
            }
        }

        // Auto-reconnect if handlers still registered and not intentionally closed
        if (notificationHandlers.size > 0 && !abort.signal.aborted) {
            const delay = getReconnectDelay();
            frontendLog.debug(
                `[engine-client] Fetch-SSE reconnecting in ${delay}ms (attempt ${reconnectAttempt})`,
                undefined,
                { source: "transport" },
            );
            wsReconnectTimer = setTimeout(() => {
                connectFetchSSE();
            }, delay);
        }
    }

    function connectWebSocket(): void {
        if (ws || fetchSSEAbort) return;

        // Tauri production: skip native WS/SSE entirely, use fetch-streaming SSE
        if (needsFetchSSE()) {
            connectFetchSSE();
            return;
        }

        // Use SSE fallback for mixed-content scenarios
        if (needsSSEFallback()) {
            connectStreamingSSEFallback();
            return;
        }

        const wsUrl = eventStreamUrl("ws");

        try {
            ws = new WebSocket(wsUrl);
        } catch {
            // WebSocket construction failed — fall back to SSE
            frontendLog.warn(
                "[engine-client] WebSocket failed, falling back to SSE",
                undefined,
                { source: "transport" },
            );
            useSSEFallback = true;
            connectStreamingSSEFallback();
            return;
        }

        ws.onopen = () => {
            frontendLog.debug("[engine-client] WebSocket connected", undefined, {
                source: "transport",
            });
            consecutiveWsFailures = 0;
            pendingHandshakeTransport = "ws";
        };

        ws.onmessage = (event) => {
            if (notificationHandlers.size === 0) return;
            consecutiveWsFailures = 0; // received data → connection is healthy

            try {
                const data = JSON.parse(
                    typeof event.data === "string" ? event.data : "",
                ) as GatewayNotificationEnvelope;

                if (!acceptGatewaySequence(data.sequence)) return;
                dispatchNotification({
                    method: data.event,
                    params: data.data,
                });
            } catch {
                // Ignore malformed messages
            }
        };

        ws.onclose = () => {
            ws = null;
            consecutiveWsFailures++;
            dispatchTransportDisconnected("ws");

            // After repeated failures without any successful message exchange,
            // fall back to SSE. This catches scenarios where native WebSocket
            // is blocked (e.g. macOS ATS in sandboxed Tauri apps) but the
            // onerror readyState check didn't trigger.
            if (
                consecutiveWsFailures >= MAX_WS_FAILURES_BEFORE_FALLBACK &&
                !useSSEFallback
            ) {
                frontendLog.warn(
                    `[engine-client] WebSocket failed ${consecutiveWsFailures} times consecutively, falling back to SSE`,
                    undefined,
                    { source: "transport" },
                );
                useSSEFallback = true;
                if (notificationHandlers.size > 0) {
                    connectStreamingSSEFallback();
                }
                return;
            }

            // Auto-reconnect with exponential backoff if any handler is still registered
            if (notificationHandlers.size > 0) {
                const delay = getReconnectDelay();
                frontendLog.debug(
                    `[engine-client] WebSocket reconnecting in ${delay}ms (attempt ${reconnectAttempt})`,
                    undefined,
                    { source: "transport" },
                );
                wsReconnectTimer = setTimeout(() => {
                    connectWebSocket();
                }, delay);
            }
        };

        ws.onerror = () => {
            // If WS fails immediately, it might be mixed content — fall back
            if (
                ws?.readyState === WebSocket.CONNECTING ||
                ws?.readyState === WebSocket.CLOSED
            ) {
                frontendLog.warn(
                    "[engine-client] WebSocket error (connection blocked), falling back to SSE",
                    undefined,
                    { source: "transport" },
                );
                useSSEFallback = true;
                ws?.close();
                ws = null;
                connectStreamingSSEFallback();
                return;
            }
            // onclose will fire after this
        };
    }

    function disconnectWebSocket(): void {
        if (wsReconnectTimer) {
            clearTimeout(wsReconnectTimer);
            wsReconnectTimer = null;
        }
        if (wsDisconnectTimer) {
            clearTimeout(wsDisconnectTimer);
            wsDisconnectTimer = null;
        }
        if (ws) {
            ws.close();
            ws = null;
        }
        if (fetchSSEAbort) {
            fetchSSEAbort.abort();
            fetchSSEAbort = null;
        }
        gatewayHandshakeComplete = false;
        pendingHandshakeTransport = null;
    }

    // ── Build EngineService via shared RPC factory ──

    return createRpcEngineService({
        call: rpc,
        getClientId: () => gatewayClientId,

        async getStatus(): Promise<EngineStatus> {
            try {
                const headers: Record<string, string> = {};
                if (token) {
                    headers["Authorization"] = `Bearer ${token}`;
                }

                const response = await fetchFn(statusUrl, {
                    headers,
                    signal: AbortSignal.timeout(5_000),
                });

                if (!response.ok) {
                    return { alive: false, ready: false };
                }

                const data = (await response.json()) as {
                    alive: boolean;
                    ready: boolean;
                };
                return { alive: data.alive, ready: data.ready };
            } catch {
                return { alive: false, ready: false };
            }
        },

        async listenNotifications(
            handler: EngineNotificationHandler,
        ): Promise<() => void> {
            notificationHandlers.add(handler);
            // Cancel any pending disconnect (React StrictMode unmount/remount)
            if (wsDisconnectTimer) {
                clearTimeout(wsDisconnectTimer);
                wsDisconnectTimer = null;
            }
            const handshake = waitForGatewayHandshake();
            connectWebSocket();
            const stop = () => {
                notificationHandlers.delete(handler);
                if (notificationHandlers.size === 0) {
                    // Debounce disconnect to survive React StrictMode rapid
                    // unmount/remount cycles. Without this, events
                    // sent during the gap are lost.
                    wsDisconnectTimer = setTimeout(() => {
                        wsDisconnectTimer = null;
                        if (notificationHandlers.size === 0) {
                            disconnectWebSocket();
                        }
                    }, 200);
                }
            };
            try {
                await handshake;
            } catch (error) {
                notificationHandlers.delete(handler);
                if (notificationHandlers.size === 0) {
                    disconnectWebSocket();
                }
                throw error;
            }
            return stop;
        },

        stopListening(): void {
            notificationHandlers.clear();
            rejectGatewayHandshake(
                new Error("Engine Gateway notification listener stopped before handshake"),
            );
            disconnectWebSocket();
        },
    });
}
